// ✅ src/pages/VehicleV2/VehicleSaleScrapEntry.jsx
// Edit fix: keep Store / Vehicle Code / Name / Type selected
// after details load, even if byStore dropdowns omit sold vehicles.

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaUpload,
  FaTrash,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/salesScrapEntry";

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const defaultFormData = {
  soldDate: "",
  storeId: "Select",
  vehicleId: "Select",
  vehicleNameId: "Select",
  vehicleSubTypeId: "Select",
  wantTo: "Sale",
  recommendBy: "",
  soldBy: "",
  scrapBy: "",
  approvedBy: "",
  partyName: "",
  brokerName: "",
  soldPrice: "",
  partyAddress: "",
  remark: "",
  salesscrapId: 0,
};

const toISODateInput = (v) => {
  if (!v) return "";
  if (v instanceof Date && !isNaN(v.getTime()))
    return v.toISOString().slice(0, 10);

  const s = toStr(v);

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [dd, mm, yy] = s.split("/").map((x) => Number(x));
    const dt = new Date(yy, mm - 1, dd);
    return isNaN(dt.getTime()) ? "" : dt.toISOString().slice(0, 10);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const dt = new Date(s);
  return isNaN(dt.getTime()) ? "" : dt.toISODateInput
    ? dt.toISOString().slice(0, 10)
    : dt.toISOString().slice(0, 10);
};

const ensureOption = (list = [], opt) => {
  const value = String(opt?.value ?? "");
  if (!value || value === "Select") return list;
  const exists = (list || []).some((x) => String(x.value) === value);
  if (exists) {
    return (list || []).map((x) =>
      String(x.value) === value
        ? {
            ...x,
            label:
              opt.label && !String(opt.label).startsWith("Vehicle")
                ? opt.label
                : x.label,
          }
        : x
    );
  }
  return [...list, { label: opt.label || value, value }];
};

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const list = toList(arr).map((x) => ({
    label: toStr(
      x.label ?? x.name ?? x.text ?? x.fld_vehicle_code ?? x.fld_vehicle_name ?? fallbackLabel,
      fallbackLabel
    ),
    value: String(
      x.value ??
        x.id ??
        x.fld_vehicleid ??
        x.fld_storeid ??
        x.key ??
        fallbackLabel
    ),
  }));
  return list.length
    ? list
    : [{ label: fallbackLabel, value: fallbackLabel }];
};

const pickFromRow = (row, keys) => {
  if (!row) return "";
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && String(row[key]).trim() !== "") {
      return String(row[key]).trim();
    }
  }
  return "";
};

const VehicleSaleScrapEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "BRANCH";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, branchType, userId]
  );

  const navState = location.state || {};
  const navRow = navState?.row?.raw || navState?.row || {};
  const editId =
    navState?.id ||
    navState?.row?.id ||
    navRow?.fld_salesscrapid ||
    navRow?.id ||
    0;
  const prefillVehicleId = navState?.vehicleId || 0;
  const vehicleOwnDetId = navState?.vehicleOwnDetId || 0;
  const isEdit = !!toInt(editId);

  const [pageLoading, setPageLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const isBusy =
    pageLoading ||
    initLoading ||
    editLoading ||
    vehicleLoading ||
    submitLoading ||
    uploadLoading;

  const [initData, setInitData] = useState({
    stores: [{ label: "Select", value: "Select" }],
    vehicleCodes: [{ label: "Select", value: "Select" }],
    vehicleNames: [{ label: "Select", value: "Select" }],
  });

  const [vehicleSubTypes, setVehicleSubTypes] = useState([
    { label: "Select", value: "Select" },
  ]);

  const [regDate, setRegDate] = useState("");
  const [docFiles, setDocFiles] = useState([]);
  const [formData, setFormData] = useState(defaultFormData);

  const didPrefillRef = useRef(false);
  const didLoadEditRef = useRef(false);

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const radioLabelClass = useMemo(
    () =>
      `flex items-center gap-2 ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const panelClass = useMemo(
    () => `p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`,
    [isDark]
  );

  const panelTitleClass = useMemo(
    () =>
      `text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`,
    [isDark]
  );

  const setField = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const validateSoldDateAgainstReg = useCallback((soldISO, regISO) => {
    if (!soldISO || !regISO) return true;
    return soldISO >= regISO;
  }, []);

  const resetVehicleSelections = useCallback(() => {
    setFormData((p) => ({
      ...p,
      vehicleId: "Select",
      vehicleNameId: "Select",
      vehicleSubTypeId: "Select",
    }));
    setVehicleSubTypes([{ label: "Select", value: "Select" }]);
    setRegDate("");
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      setInitLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: sessionBranchId,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to load init");
        return;
      }

      setInitData({
        stores: normalizeOptionList(data.stores, "Select"),
        vehicleCodes: normalizeOptionList(data.vehicleCodes, "Select"),
        vehicleNames: normalizeOptionList(data.vehicleNames, "Select"),
      });

      setVehicleSubTypes([{ label: "Select", value: "Select" }]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Init failed");
    } finally {
      setInitLoading(false);
      setPageLoading(false);
    }
  }, [sessionBranchId, branchType, loginUserId, commonHeaders]);

  const loadVehiclesByStore = useCallback(
    async (storeId, { resetSelections = true } = {}) => {
      const sid = String(storeId || "Select");

      if (resetSelections) {
        setFormData((p) => ({ ...p, storeId: sid }));
        resetVehicleSelections();
      } else {
        setFormData((p) => ({ ...p, storeId: sid }));
      }

      if (isSelect(sid)) return;

      try {
        setVehicleLoading(true);

        const { data } = await axios.get(`${API_BASE}/vehicles/byStore`, {
          params: {
            branchId: sessionBranchId,
            branchType,
            storeId: sid,
          },
          headers: commonHeaders,
        });

        setInitData((prev) => ({
          ...prev,
          vehicleCodes: normalizeOptionList(data?.vehicleCodes, "Select"),
          vehicleNames: normalizeOptionList(data?.vehicleNames, "Select"),
        }));
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            "Store vehicles load failed"
        );
      } finally {
        setVehicleLoading(false);
      }
    },
    [sessionBranchId, branchType, commonHeaders, resetVehicleSelections]
  );

  const loadVehicleDetails = useCallback(
    async (vehicleId, storeIdForCheck, { keepSubType = false } = {}) => {
      const vid = String(vehicleId || "Select");
      const sid = String(storeIdForCheck ?? "Select");

      if (isSelect(sid)) {
        toast.warning("Please select Store first");
        if (!keepSubType) resetVehicleSelections();
        return false;
      }

      setFormData((p) => ({
        ...p,
        vehicleId: vid,
        vehicleNameId: vid,
        vehicleSubTypeId: keepSubType ? p.vehicleSubTypeId : "Select",
      }));

      if (!keepSubType) {
        setVehicleSubTypes([{ label: "Select", value: "Select" }]);
      }
      setRegDate("");

      if (isSelect(vid)) return false;

      try {
        setVehicleLoading(true);

        const [subRs, regRs] = await Promise.all([
          axios.get(`${API_BASE}/vehicle/subtype`, {
            params: { branchId: sessionBranchId, vehicleId: vid },
            headers: commonHeaders,
          }),
          axios.get(`${API_BASE}/vehicle/regDate`, {
            params: { vehicleId: vid },
            headers: commonHeaders,
          }),
        ]);

        const subtypeList = normalizeOptionList(
          subRs.data?.vehicleSubTypes,
          "Select"
        );
        setVehicleSubTypes(subtypeList);

        const r = toISODateInput(regRs.data?.regDate || "");
        setRegDate(r);
        return true;
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            "Vehicle details load failed"
        );
        return false;
      } finally {
        setVehicleLoading(false);
      }
    },
    [sessionBranchId, commonHeaders, resetVehicleSelections]
  );

  const handleUploadDocs = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;

      try {
        setUploadLoading(true);

        const fd = new FormData();
        Array.from(files).forEach((f) => fd.append("files", f));

        const { data } = await axios.post(`${API_BASE}/upload`, fd, {
          headers: {
            ...commonHeaders,
            "Content-Type": "multipart/form-data",
          },
        });

        const uploaded = toList(data?.files);
        setDocFiles((prev) => [...prev, ...uploaded]);
        toast.success("Document(s) uploaded");
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "Upload failed"
        );
      } finally {
        setUploadLoading(false);
      }
    },
    [commonHeaders]
  );

  const removeDoc = useCallback((idx) => {
    setDocFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleChange = useCallback(
    async (e) => {
      const { name, value, type, files } = e.target;

      if (name === "storeId") {
        await loadVehiclesByStore(value, { resetSelections: true });
        return;
      }

      if (name === "vehicleId" || name === "vehicleNameId") {
        await loadVehicleDetails(value, formData.storeId, {
          keepSubType: false,
        });
        return;
      }

      if (type === "file") {
        await handleUploadDocs(files);
        e.target.value = "";
        return;
      }

      if (name === "soldDate") {
        if (regDate && value && !validateSoldDateAgainstReg(value, regDate)) {
          toast.error(
            `Sold/Scrap Date must be >= Registration Date (${regDate})`
          );
        }
      }

      setField(name, value);
    },
    [
      formData.storeId,
      regDate,
      setField,
      loadVehiclesByStore,
      loadVehicleDetails,
      handleUploadDocs,
      validateSoldDateAgainstReg,
    ]
  );

  const applyVehicleOptions = useCallback((storeId, vehicleId, codeLabel, nameLabel, subTypeId, subTypeLabel) => {
    setInitData((prev) => ({
      ...prev,
      stores: ensureOption(prev.stores, {
        value: storeId,
        label: `Store (${storeId})`,
      }),
      vehicleCodes: ensureOption(prev.vehicleCodes, {
        value: vehicleId,
        label: codeLabel || `Vehicle Code (${vehicleId})`,
      }),
      vehicleNames: ensureOption(prev.vehicleNames, {
        value: vehicleId,
        label: nameLabel || `Vehicle Name (${vehicleId})`,
      }),
    }));

    if (subTypeId && !isSelect(subTypeId)) {
      setVehicleSubTypes((prev) =>
        ensureOption(prev, {
          value: subTypeId,
          label: subTypeLabel || `Type (${subTypeId})`,
        })
      );
    }
  }, []);

  const loadEditData = useCallback(async () => {
    if (!isEdit || !editId || didLoadEditRef.current) return;

    try {
      didLoadEditRef.current = true;
      setEditLoading(true);

      const [detailsRes, docsRes] = await Promise.all([
        axios.get(`${API_BASE}/details`, {
          params: { salesscrapId: editId },
          headers: commonHeaders,
        }),
        axios.get(`${API_BASE}/docs`, {
          params: { salesscrapId: editId },
          headers: commonHeaders,
        }),
      ]);

      const found = detailsRes.data?.found;
      const row = detailsRes.data?.row || navRow || {};

      if (!found && !navRow?.fld_salesscrapid && !navRow?.id) {
        toast.error("Edit record not found");
        return;
      }

      const storeId =
        pickFromRow(row, ["fld_storeid", "storeId", "store"]) ||
        pickFromRow(navRow, ["fld_storeid", "storeId"]) ||
        "Select";

      const vehicleId =
        pickFromRow(row, ["fld_vehicleid", "vehicleId", "id"]) ||
        pickFromRow(navRow, ["fld_vehicleid", "vehicleId"]) ||
        "Select";

      const subTypeId =
        pickFromRow(row, [
          "fld_vehicletypeid",
          "fld_vehicle_subtypeid",
          "fld_vehiclemaintypeid",
          "vehicleSubTypeId",
        ]) ||
        pickFromRow(navRow, [
          "fld_vehiclemaintypeid",
          "fld_vehicletypeid",
          "VehicleType",
        ]) ||
        "Select";

      const soldDate = toISODateInput(
        row.entrydate || row.fld_solddate || row.soldDate
      );

      const soldFlag = toStr(
        row.fld_sold_sales || row.salesScrap || navRow.fld_sold_sales || navRow.salesScrap
      ).toLowerCase();

      const wantTo = soldFlag === "sales" || soldFlag === "sale" ? "Sale" : "Scrap";

      const codeLabel =
        pickFromRow(row, ["fld_vehicle_code", "vehicleCode"]) ||
        pickFromRow(navRow, ["fld_vehicle_code", "vehicleCode"]);
      const nameLabel =
        pickFromRow(row, ["fld_vehicle_name", "vehicleName"]) ||
        pickFromRow(navRow, ["fld_vehicle_name", "vehicleName"]);
      const typeLabel =
        pickFromRow(row, ["VehicleType", "fld_vehicletypename", "vehicleMainType"]) ||
        pickFromRow(navRow, ["VehicleType", "vehicleMainType"]);

      if (!isSelect(storeId)) {
        await loadVehiclesByStore(storeId, { resetSelections: false });
      }

      applyVehicleOptions(
        storeId,
        vehicleId,
        codeLabel,
        nameLabel,
        subTypeId,
        typeLabel
      );

      if (!isSelect(vehicleId) && !isSelect(storeId)) {
        await loadVehicleDetails(vehicleId, storeId, { keepSubType: true });
      }

      applyVehicleOptions(
        storeId,
        vehicleId,
        codeLabel,
        nameLabel,
        subTypeId,
        typeLabel
      );

      setFormData((p) => ({
        ...p,
        salesscrapId: Number(row.fld_salesscrapid || editId || 0),
        soldDate,
        storeId: isSelect(storeId) ? p.storeId : storeId,
        vehicleId: isSelect(vehicleId) ? p.vehicleId : vehicleId,
        vehicleNameId: isSelect(vehicleId) ? p.vehicleNameId : vehicleId,
        vehicleSubTypeId: isSelect(subTypeId) ? p.vehicleSubTypeId : subTypeId,
        wantTo,
        recommendBy: toStr(row.fld_recommendby),
        soldBy: toStr(row.fld_soldby),
        scrapBy: toStr(row.fld_scrapby),
        approvedBy: toStr(row.fld_approvedby),
        partyName: toStr(row.fld_partyname),
        brokerName: toStr(row.fld_brokername),
        soldPrice: toStr(row.fld_soldprice),
        partyAddress: toStr(row.fld_partyaddress),
        remark: toStr(row.fld_remark),
      }));

      const docs = toList(docsRes.data?.docs)
        .map((x) => toStr(x))
        .filter(Boolean)
        .map((fileName) => ({
          fileName,
          originalName: fileName,
          relativePath: `SaleorScrap/${fileName}`,
        }));

      setDocFiles(docs);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Edit load failed"
      );
    } finally {
      setEditLoading(false);
    }
  }, [
    isEdit,
    editId,
    commonHeaders,
    navRow,
    loadVehiclesByStore,
    loadVehicleDetails,
    applyVehicleOptions,
  ]);

  const runPrefillVehicle = useCallback(async () => {
    if (isEdit || !prefillVehicleId || didPrefillRef.current) return;

    try {
      didPrefillRef.current = true;
      const vid = String(prefillVehicleId);

      applyVehicleOptions("Select", vid, `Vehicle Code (${vid})`, `Vehicle Name (${vid})`);

      setFormData((p) => ({
        ...p,
        vehicleId: vid,
        vehicleNameId: vid,
      }));

      try {
        const { data } = await axios.get(`${API_BASE}/vehicle/prefill`, {
          params: { vehicleId: vid },
          headers: commonHeaders,
        });

        const row = data?.row;
        if (data?.found && row?.fld_storeid) {
          const storeId = String(row.fld_storeid);
          const realVid = String(row.fld_vehicleid ?? vid);

          setFormData((p) => ({
            ...p,
            storeId,
            vehicleId: realVid,
            vehicleNameId: realVid,
          }));

          await loadVehiclesByStore(storeId, { resetSelections: false });
          applyVehicleOptions(
            storeId,
            realVid,
            row.fld_vehicle_code,
            row.fld_vehicle_name
          );
          await loadVehicleDetails(realVid, storeId, { keepSubType: true });
        } else {
          toast.info("Vehicle prefilled. Please select Store to load subtype.");
        }
      } catch {
        toast.info("Vehicle prefilled. Please select Store to load subtype.");
      }
    } catch (err) {
      console.error(err);
    }
  }, [
    isEdit,
    prefillVehicleId,
    commonHeaders,
    loadVehiclesByStore,
    loadVehicleDetails,
    applyVehicleOptions,
  ]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (isSelect(formData.storeId)) return toast.error("Please select Store");
      if (isSelect(formData.vehicleId))
        return toast.error("Please select Vehicle Code/Name");
      if (!formData.soldDate) return toast.error("Please select Sold/Scrap Date");
      if (isSelect(formData.vehicleSubTypeId))
        return toast.error("Please select Vehicle Type/Subtype");
      if (!formData.approvedBy) return toast.error("Approved By is required");
      if (!formData.partyName) return toast.error("Party Name is required");

      if (formData.wantTo === "Sale" && !formData.soldBy) {
        return toast.error("Sold By is required");
      }

      if (formData.wantTo === "Scrap" && !formData.scrapBy) {
        return toast.error("Scrap By is required");
      }

      if (regDate && !validateSoldDateAgainstReg(formData.soldDate, regDate)) {
        return toast.error(
          `Sold/Scrap Date must be >= Registration Date (${regDate})`
        );
      }

      const docs = docFiles.map((d) => d.fileName).filter(Boolean);

      const payloadBase = {
        loginUserId,
        branchId: sessionBranchId,
        storeId: formData.storeId,
        vehicleId: formData.vehicleId,
        vehicleSubTypeId: formData.vehicleSubTypeId,
        soldDate: formData.soldDate,
        recommendBy: formData.recommendBy,
        approvedBy: formData.approvedBy,
        soldPrice: formData.soldPrice,
        partyName: formData.partyName,
        partyAddress: formData.partyAddress,
        brokerName: formData.brokerName,
        remark: formData.remark,
        docs,
        salesscrapId: formData.salesscrapId || editId || 0,
        vehicleOwnDetId,
        wantTo: formData.wantTo,
        salesScrap: formData.wantTo === "Sale" ? "Sales" : "scrap",
      };

      const isSale = formData.wantTo === "Sale";
      const url = isSale
        ? `${API_BASE}/submit/sale`
        : `${API_BASE}/submit/scrap`;

      const payload = isSale
        ? { ...payloadBase, soldBy: formData.soldBy }
        : { ...payloadBase, scrapBy: formData.scrapBy };

      try {
        setSubmitLoading(true);

        const { data } = await axios.post(url, payload, {
          headers: commonHeaders,
        });

        toast.success(
          data?.message || (isEdit ? "Updated Successfully" : "Saved Successfully")
        );

        setTimeout(() => {
          navigate("/vehicle/Vehicle_SalesandScrapListing");
        }, 600);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "Submit failed"
        );
      } finally {
        setSubmitLoading(false);
      }
    },
    [
      formData,
      regDate,
      docFiles,
      loginUserId,
      sessionBranchId,
      vehicleOwnDetId,
      isEdit,
      editId,
      commonHeaders,
      validateSoldDateAgainstReg,
      navigate,
    ]
  );

  const handleRefresh = useCallback(async () => {
    await loadInit();

    if (isEdit) {
      didLoadEditRef.current = false;
      await loadEditData();
      return;
    }

    if (prefillVehicleId) {
      didPrefillRef.current = false;
      await runPrefillVehicle();
    }
  }, [loadInit, isEdit, loadEditData, prefillVehicleId, runPrefillVehicle]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (initLoading) return;
    if (!isEdit) return;
    loadEditData();
  }, [initLoading, isEdit, loadEditData]);

  useEffect(() => {
    if (initLoading) return;
    if (isEdit) return;
    if (!prefillVehicleId) return;
    runPrefillVehicle();
  }, [initLoading, isEdit, prefillVehicleId, runPrefillVehicle]);

  const showSaleFields = formData.wantTo === "Sale";
  const showScrapFields = formData.wantTo === "Scrap";

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 max-w-full overflow-x-hidden ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">VEHICLE SALE AND SCRAP</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border max-w-full overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Vehicle Sales and Scrap Entry
                {isEdit ? ` (Edit #${editId})` : ""}
              </h2>
              <p
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {sessionBranchId || "-"} • User ID:{" "}
                {loginUserId || "-"} • Mode: {isEdit ? "EDIT" : "NEW"}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleSubmit}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass} ${
                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaSave />
                {submitLoading
                  ? isEdit
                    ? "Updating..."
                    : "Submitting..."
                  : isEdit
                  ? "Update"
                  : "Submit"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "bg-gray-700 text-white border-gray-600"
                    : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "bg-gray-700 text-white border-gray-600"
                    : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {pageLoading || initLoading || editLoading ? (
            <div
              className={`p-16 text-center ${
                isDark ? "bg-gray-800" : "bg-gray-50"
              }`}
            >
              <div
                className={`text-lg font-medium ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {editLoading ? "Loading edit data..." : "Loading..."}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className={panelClass}>
                <h3 className={panelTitleClass}>Vehicle Details</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Sold/Scrap Date *</label>
                    <input
                      type="date"
                      name="soldDate"
                      value={formData.soldDate}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    />
                    {regDate ? (
                      <div
                        className={`mt-2 text-xs ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Registration Date:{" "}
                        <span className="font-semibold">{regDate}</span>
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass}>Store *</label>
                    <select
                      name="storeId"
                      value={formData.storeId}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    >
                      {(initData.stores || []).map((s, idx) => (
                        <option key={`${s.value}-${idx}`} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Code *</label>
                    <select
                      name="vehicleId"
                      value={formData.vehicleId}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    >
                      {(initData.vehicleCodes || []).map((v, idx) => (
                        <option key={`${v.value}-${idx}`} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Name *</label>
                    <select
                      name="vehicleNameId"
                      value={formData.vehicleNameId}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    >
                      {(initData.vehicleNames || []).map((v, idx) => (
                        <option key={`${v.value}-${idx}`} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Type/Subtype *</label>
                    <select
                      name="vehicleSubTypeId"
                      value={formData.vehicleSubTypeId}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy || isSelect(formData.vehicleId)}
                    >
                      {(vehicleSubTypes || []).map((v, idx) => (
                        <option key={`${v.value}-${idx}`} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Want To</label>
                    <div className="flex gap-6 pt-2">
                      <label className={radioLabelClass}>
                        <input
                          type="radio"
                          name="wantTo"
                          value="Sale"
                          checked={formData.wantTo === "Sale"}
                          onChange={handleChange}
                          disabled={isBusy}
                        />
                        Sale
                      </label>
                      <label className={radioLabelClass}>
                        <input
                          type="radio"
                          name="wantTo"
                          value="Scrap"
                          checked={formData.wantTo === "Scrap"}
                          onChange={handleChange}
                          disabled={isBusy}
                        />
                        Scrap
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className={panelClass}>
                <h3 className={panelTitleClass}>Approval & Party Details</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Recommend By</label>
                    <input
                      type="text"
                      name="recommendBy"
                      value={formData.recommendBy}
                      onChange={handleChange}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  {showSaleFields ? (
                    <div>
                      <label className={labelClass}>Sold By *</label>
                      <input
                        type="text"
                        name="soldBy"
                        value={formData.soldBy}
                        onChange={handleChange}
                        className={inputClass}
                        required
                        disabled={isBusy}
                      />
                    </div>
                  ) : null}

                  {showScrapFields ? (
                    <div>
                      <label className={labelClass}>Scrap By *</label>
                      <input
                        type="text"
                        name="scrapBy"
                        value={formData.scrapBy}
                        onChange={handleChange}
                        className={inputClass}
                        required
                        disabled={isBusy}
                      />
                    </div>
                  ) : null}

                  <div>
                    <label className={labelClass}>Approved By *</label>
                    <input
                      type="text"
                      name="approvedBy"
                      value={formData.approvedBy}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Party Name *</label>
                    <input
                      type="text"
                      name="partyName"
                      value={formData.partyName}
                      onChange={handleChange}
                      className={inputClass}
                      required
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Broker Name (if any)</label>
                    <input
                      type="text"
                      name="brokerName"
                      value={formData.brokerName}
                      onChange={handleChange}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Sold Price</label>
                    <input
                      type="number"
                      name="soldPrice"
                      value={formData.soldPrice}
                      onChange={handleChange}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Party Address</label>
                    <textarea
                      name="partyAddress"
                      value={formData.partyAddress}
                      onChange={handleChange}
                      rows={3}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Remarks / Reason</label>
                    <textarea
                      name="remark"
                      value={formData.remark}
                      onChange={handleChange}
                      rows={3}
                      className={inputClass}
                      disabled={isBusy}
                    />
                  </div>
                </div>
              </div>

              <div className={panelClass}>
                <h3 className={panelTitleClass}>Documents</h3>

                <label
                  className={`cursor-pointer ${inputClass} flex items-center gap-2 ${
                    isBusy ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <FaUpload className="w-4 h-4" />
                  <span className="truncate">
                    {uploadLoading ? "Uploading..." : "Choose files..."}
                  </span>
                  <input
                    type="file"
                    name="docsUpload"
                    onChange={handleChange}
                    className="hidden"
                    multiple
                    disabled={isBusy}
                  />
                </label>

                {docFiles.length > 0 ? (
                  <div className="mt-4 overflow-x-auto rounded-lg border shadow-lg">
                    <table
                      className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}
                    >
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-semibold">
                            File
                          </th>
                          <th className="px-6 py-4 text-center text-sm font-semibold">
                            Open
                          </th>
                          <th className="px-6 py-4 text-center text-sm font-semibold">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody
                        className={`${
                          isDark ? "divide-gray-700" : "divide-gray-200"
                        } divide-y`}
                      >
                        {docFiles.map((d, idx) => (
                          <tr key={`${d.fileName}-${idx}`}>
                            <td className="px-6 py-4 text-sm">
                              {d.originalName || d.fileName}
                            </td>
                            <td className="px-6 py-4 text-center">
                              {d.relativePath ? (
                                <a
                                  href={`/${d.relativePath}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 text-sm font-medium"
                                >
                                  Open
                                </a>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                type="button"
                                onClick={() => removeDoc(idx)}
                                disabled={isBusy}
                                className="text-red-500"
                              >
                                <FaTrash className="inline w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    className={`mt-4 p-6 text-center text-sm ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No documents uploaded
                  </div>
                )}
              </div>

              <div className="p-4">
                <button
                  type="submit"
                  disabled={isBusy}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white ${
                    isBusy ? "opacity-50 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass}`}
                >
                  <FaSave />
                  {isEdit ? "Update" : "Submit"}
                </button>
              </div>
            </form>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSaleScrapEntry;